import React from "react";
import { Box, Typography, Link, List, ListItem, ListItemText, Divider } from "@mui/material";

interface NotionBlock {
  id: string;
  type: string;
  [key: string]: any;
}

interface NotionBlockRendererProps {
  blocks: NotionBlock[];
}

function NotionBlockRenderer({ blocks }: NotionBlockRendererProps) {
  const renderBlock = (block: NotionBlock) => {
    const { type, id } = block;

    switch (type) {
      case "paragraph":
        return (
          <Typography key={id} variant="body1" paragraph>
            {renderRichText(block.paragraph?.richText || [])}
          </Typography>
        );

      case "heading_1":
        return (
          <Typography key={id} variant="h3" component="h1" gutterBottom sx={{ mt: 4, mb: 2 }}>
            {renderRichText(block.heading_1?.richText || block.heading1?.richText || [])}
          </Typography>
        );

      case "heading_2":
        return (
          <Typography key={id} variant="h4" component="h2" gutterBottom sx={{ mt: 3, mb: 1.5 }}>
            {renderRichText(block.heading_2?.richText || block.heading2?.richText || [])}
          </Typography>
        );

      case "heading_3":
        return (
          <Typography key={id} variant="h5" component="h3" gutterBottom sx={{ mt: 2, mb: 1 }}>
            {renderRichText(block.heading_3?.richText || block.heading3?.richText || [])}
          </Typography>
        );

      case "bulleted_list_item":
        return (
          <ListItem key={id} sx={{ display: "list-item", pl: 0 }}>
            <ListItemText
              primary={renderRichText(block.bulleted_list_item?.richText || block.bulletedListItem?.richText || [])}
            />
          </ListItem>
        );

      case "numbered_list_item":
        return (
          <ListItem key={id} sx={{ display: "list-item", pl: 0 }}>
            <ListItemText
              primary={renderRichText(block.numbered_list_item?.richText || block.numberedListItem?.richText || [])}
            />
          </ListItem>
        );

      case "quote":
        return (
          <Box
            key={id}
            sx={{
              borderLeft: 4,
              borderColor: "primary.main",
              pl: 2,
              my: 2,
              bgcolor: "grey.50",
              py: 1,
            }}
          >
            <Typography variant="body1" fontStyle="italic">
              {renderRichText(block.quote?.richText || [])}
            </Typography>
          </Box>
        );

      case "code":
        return (
          <Box
            key={id}
            component="pre"
            sx={{
              bgcolor: "grey.100",
              p: 2,
              borderRadius: 1,
              overflow: "auto",
              fontFamily: "monospace",
              fontSize: "0.875rem",
              my: 2,
            }}
          >
            <code>{block.code?.richText?.[0]?.plainText || ""}</code>
          </Box>
        );

      case "divider":
        return <Divider key={id} sx={{ my: 3 }} />;

      case "image":
        const imageUrl = block.image?.file?.url || block.image?.external?.url;
        const caption = block.image?.caption?.[0]?.plainText;
        return (
          <Box key={id} sx={{ my: 3, textAlign: "center" }}>
            {imageUrl && (
              <img
                src={imageUrl}
                alt={caption || "Image"}
                style={{ maxWidth: "100%", height: "auto", borderRadius: "8px" }}
              />
            )}
            {caption && (
              <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                {caption}
              </Typography>
            )}
          </Box>
        );

      case "callout":
        return (
          <Box
            key={id}
            sx={{
              display: "flex",
              bgcolor: "info.light",
              p: 2,
              borderRadius: 1,
              my: 2,
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ mr: 1, fontSize: "1.2rem" }}>{block.callout?.icon?.emoji || "💡"}</Box>
            <Typography variant="body1">{renderRichText(block.callout?.richText || [])}</Typography>
          </Box>
        );

      default:
        return (
          <Typography key={id} variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            [Unsupported block type: {type}]
          </Typography>
        );
    }
  };

  const renderRichText = (richTextArray: any[]) => {
    return richTextArray.map((richText, index) => {
      const { annotations, plainText, href } = richText;
      let element = plainText;

      if (href) {
        element = (
          <Link key={index} href={href} target="_blank" rel="noopener noreferrer">
            {plainText}
          </Link>
        );
      }

      if (annotations?.bold) {
        element = <strong key={index}>{element}</strong>;
      }
      if (annotations?.italic) {
        element = <em key={index}>{element}</em>;
      }
      if (annotations?.strikethrough) {
        element = <del key={index}>{element}</del>;
      }
      if (annotations?.underline) {
        element = <u key={index}>{element}</u>;
      }
      if (annotations?.code) {
        element = (
          <Box
            key={index}
            component="code"
            sx={{
              bgcolor: "grey.200",
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontFamily: "monospace",
              fontSize: "0.875em",
            }}
          >
            {element}
          </Box>
        );
      }

      return element;
    });
  };

  const groupedBlocks: Array<NotionBlock | { type: "list"; listType: string; items: NotionBlock[] }> = [];
  let currentListItems: NotionBlock[] = [];
  let currentListType: string | null = null;

  for (const block of blocks) {
    if (block.type === "bulleted_list_item" || block.type === "numbered_list_item") {
      if (currentListType !== block.type) {
        if (currentListItems.length > 0 && currentListType) {
          groupedBlocks.push({
            type: "list",
            listType: currentListType,
            items: currentListItems,
          });
        }
        currentListItems = [block];
        currentListType = block.type;
      } else {
        currentListItems.push(block);
      }
    } else {
      if (currentListItems.length > 0 && currentListType) {
        groupedBlocks.push({
          type: "list",
          listType: currentListType,
          items: currentListItems,
        });
        currentListItems = [];
        currentListType = null;
      }
      groupedBlocks.push(block);
    }
  }

  if (currentListItems.length > 0 && currentListType) {
    groupedBlocks.push({
      type: "list",
      listType: currentListType,
      items: currentListItems,
    });
  }

  return (
    <Box>
      {groupedBlocks.map((item, index) => {
        if (item.type === "list") {
          const ListComponent = item.listType === "numbered_list_item" ? "ol" : "ul";
          return (
            <List key={index} component={ListComponent} sx={{ pl: 2 }}>
              {item.items.map((block: NotionBlock) => renderBlock(block))}
            </List>
          );
        }
        return renderBlock(item as NotionBlock);
      })}
    </Box>
  );
}

export default NotionBlockRenderer;
